/**
 * Centralized role-checking helpers for the Staff Hub.
 * 
 * Role hierarchy:
 *   Owner > Admin > Manager > Supervisor > Staff > Trainee
 *   Special: Training-only, POS-only (restricted access)
 * 
 * Access tiers:
 *   isOwnerOrAdmin  – full access to everything including sensitive HR/payroll data
 *   isManagerPlus   – operational access (Owner, Admin, Manager)
 *   isSupervisorPlus – supervisory access (Owner, Admin, Manager, Supervisor)
 */

const OWNER_ADMIN_ROLES = ['Owner', 'Admin'];
const MANAGER_PLUS_ROLES = ['Owner', 'Admin', 'Manager'];
const SUPERVISOR_PLUS_ROLES = ['Owner', 'Admin', 'Manager', 'Supervisor'];
const RESTRICTED_ROLES = ['Training-only', 'POS-only'];
const SPECIAL_ADMIN_ROLES = ['Training Admin', 'Course Admin', 'Policy Admin'];

export function isOwnerOrAdmin(role?: string): boolean {
  return OWNER_ADMIN_ROLES.includes(role || '');
}

export function isManagerPlus(role?: string): boolean {
  return MANAGER_PLUS_ROLES.includes(role || '');
}

export function isSupervisorPlus(role?: string): boolean {
  return SUPERVISOR_PLUS_ROLES.includes(role || '');
}

export function isTrainingOnly(role?: string): boolean {
  return role === 'Training-only';
}

export function isPosOnly(role?: string): boolean {
  return role === 'POS-only';
}

export function isRestrictedRole(role?: string): boolean {
  return RESTRICTED_ROLES.includes(role || '');
}

export function isCourseAdmin(role?: string): boolean { return ['Owner','Admin','Training Admin','Course Admin'].includes(role||''); }
export function isPolicyAdmin(role?: string): boolean { return ['Owner','Admin','Training Admin','Policy Admin'].includes(role||''); }

/** Throw Forbidden if user is not at least manager-level (Owner/Admin/Manager) */
export function requireManagerPlus(role?: string): void {
  if (!isManagerPlus(role)) throw new Error('Forbidden');
}

/** Throw Forbidden if user is not Owner or Admin */
export function requireOwnerOrAdmin(role?: string): void {
  if (!isOwnerOrAdmin(role)) throw new Error('Forbidden');
}

/** Throw Forbidden if user is not at least supervisor-level */
export function requireSupervisorPlus(role?: string): void {
  if (!isSupervisorPlus(role)) throw new Error('Forbidden');
}

/**
 * Comprehensive permission matrix for all roles.
 * Each entry describes what a role can access.
 */
export type PermissionArea = 
  | 'dashboard' | 'profile' | 'onboarding' | 'training' | 'compliance'
  | 'handbook' | 'sops' | 'policies' | 'documents' | 'certificates'
  | 'journal' | 'reviews' | 'customerReviews' | 'trainingRequests'
  | 'uniform' | 'keys' | 'rewards' | 'benefits' | 'suggestions'
  | 'employeeOfMonth' | 'quizHistory' | 'pubInfo' | 'floorPlans'
  | 'healthSafety' | 'tools' | 'sharedDocs' | 'announcements'
  | 'messages' | 'personalTasks' | 'leaveRequests' | 'ask'
  | 'trainingBuilder' | 'onboardingJourneys' | 'complianceCerts' | 'infoHub'
  | 'recognition' | 'feedbackCentre' | 'documentLibrary'
  | 'managerDashboard' | 'teamView' | 'teamReadiness' | 'managerTasks'
  | 'firelog' | 'coshh' | 'maintenance' | 'complianceExpiry'
  | 'trainingAnalytics' | 'leaveManagement'
  | 'admin' | 'adminEmployees' | 'adminRewards' | 'adminPolicies'
  | 'adminModules' | 'adminCompliance' | 'adminHandbook'
  | 'dataBackup' | 'bridgeHealth' | 'devBoard' | 'emailSafety'
  | 'permissionMatrix' | 'documentAccessLog' | 'dailyOpsBridge'
  | 'systemDocs' | 'handoverDocs' | 'actions'
  | 'reports' | 'engagementInsights' | 'auditReadiness' | 'managerOps';

export interface RolePermission {
  role: string;
  areas: PermissionArea[];
  description: string;
  canEditOtherStaff: boolean;
  canViewSensitiveHR: boolean;
  canApproveLeave: boolean;
  canExportData: boolean;
}

const STAFF_AREAS: PermissionArea[] = [
  'dashboard', 'profile', 'onboarding', 'training', 'compliance',
  'handbook', 'sops', 'policies', 'documents', 'certificates',
  'journal', 'reviews', 'customerReviews', 'trainingRequests',
  'uniform', 'keys', 'rewards', 'benefits', 'suggestions',
  'employeeOfMonth', 'quizHistory', 'pubInfo', 'floorPlans',
  'healthSafety', 'tools', 'sharedDocs', 'announcements',
  'messages', 'personalTasks', 'leaveRequests', 'ask', 'actions',
  'trainingBuilder', 'onboardingJourneys', 'complianceCerts', 'infoHub',
  'recognition', 'feedbackCentre', 'documentLibrary',
];

const SUPERVISOR_AREAS: PermissionArea[] = [
  ...STAFF_AREAS,
  'teamView',
];

const MANAGER_AREAS: PermissionArea[] = [
  ...SUPERVISOR_AREAS,
  'managerDashboard', 'teamReadiness', 'managerTasks',
  'firelog', 'coshh', 'maintenance', 'complianceExpiry',
  'trainingAnalytics', 'leaveManagement',
  'dailyOpsBridge', 'systemDocs', 'handoverDocs',
  'reports', 'engagementInsights', 'managerOps',
];

const ADMIN_AREAS: PermissionArea[] = [
  ...MANAGER_AREAS,
  'admin', 'adminEmployees', 'adminRewards', 'adminPolicies',
  'adminModules', 'adminCompliance', 'adminHandbook',
  'dataBackup', 'bridgeHealth', 'emailSafety',
  'permissionMatrix', 'documentAccessLog',
  'auditReadiness',
];

const OWNER_AREAS: PermissionArea[] = [
  ...ADMIN_AREAS,
  'devBoard',
];

export const PERMISSION_MATRIX: RolePermission[] = [
  {
    role: 'Owner',
    areas: OWNER_AREAS,
    description: 'Full access to all features including dev tools, sensitive HR data, and system configuration.',
    canEditOtherStaff: true,
    canViewSensitiveHR: true,
    canApproveLeave: true,
    canExportData: true,
  },
  {
    role: 'Admin',
    areas: ADMIN_AREAS,
    description: 'Full operational and admin access. Can view sensitive HR data and manage all staff.',
    canEditOtherStaff: true,
    canViewSensitiveHR: true,
    canApproveLeave: true,
    canExportData: true,
  },
  {
    role: 'Manager',
    areas: MANAGER_AREAS,
    description: 'Team management, operational tools, training analytics, and leave management. Cannot view sensitive HR/payroll data.',
    canEditOtherStaff: true,
    canViewSensitiveHR: false,
    canApproveLeave: true,
    canExportData: true,
  },
  {
    role: 'Supervisor',
    areas: SUPERVISOR_AREAS,
    description: 'Staff areas plus limited team view. Cannot manage operations, approve leave, or view sensitive data.',
    canEditOtherStaff: false,
    canViewSensitiveHR: false,
    canApproveLeave: false,
    canExportData: false,
  },
  {
    role: 'Staff',
    areas: STAFF_AREAS,
    description: 'Own-data access only. Training, compliance, documents, requests, and personal tools.',
    canEditOtherStaff: false,
    canViewSensitiveHR: false,
    canApproveLeave: false,
    canExportData: false,
  },
  {
    role: 'Trainee',
    areas: STAFF_AREAS,
    description: 'Same as Staff. New starter with full own-data access.',
    canEditOtherStaff: false,
    canViewSensitiveHR: false,
    canApproveLeave: false,
    canExportData: false,
  },
  {
    role: 'Course Admin',
    areas: ['dashboard','profile','training','certificates','trainingBuilder','trainingAnalytics'],
    description: 'Course, content, enrolment, assessment and certificate administration without HR or policy administration.',
    canEditOtherStaff: false, canViewSensitiveHR: false, canApproveLeave: false, canExportData: true,
  },
  {
    role: 'Policy Admin',
    areas: ['dashboard','profile','policies','sops','handbook','documentLibrary'],
    description: 'Policy, SOP and handbook administration without course, payroll or general HR administration.',
    canEditOtherStaff: false, canViewSensitiveHR: false, canApproveLeave: false, canExportData: true,
  },
  {
    role: 'Training Admin',
    areas: ['dashboard','profile','training','certificates','trainingBuilder','trainingAnalytics','policies','sops','handbook','documentLibrary'],
    description: 'Combined training and controlled-document administration without payroll or general HR administration.',
    canEditOtherStaff: false, canViewSensitiveHR: false, canApproveLeave: false, canExportData: true,
  },
  {
    role: 'Training-only',
    areas: ['dashboard', 'profile', 'training', 'compliance', 'handbook', 'sops', 'certificates', 'quizHistory', 'ask'],
    description: 'Restricted to training and compliance areas only. No HR, documents, or operational access.',
    canEditOtherStaff: false,
    canViewSensitiveHR: false,
    canApproveLeave: false,
    canExportData: false,
  },
  {
    role: 'POS-only',
    areas: ['dashboard', 'profile', 'pubInfo', 'tools', 'healthSafety', 'announcements', 'sharedDocs'],
    description: 'Restricted to POS-related areas: pub info, tools, health & safety, and shared documents only.',
    canEditOtherStaff: false,
    canViewSensitiveHR: false,
    canApproveLeave: false,
    canExportData: false,
  },
];

export function getPermissionsForRole(role?: string): RolePermission | undefined {
  return PERMISSION_MATRIX.find(p => p.role === role);
}

export function hasAccessToArea(role: string | undefined, area: PermissionArea): boolean {
  const perms = getPermissionsForRole(role);
  if (!perms) {
    // Default to Staff for unknown roles
    const staff = PERMISSION_MATRIX.find(p => p.role === 'Staff');
    return staff?.areas.includes(area) ?? false;
  }
  return perms.areas.includes(area);
}

/**
 * Sensitive fields that only Owner/Admin may view or edit.
 * Managers must never see or modify these.
 */
export const SENSITIVE_FIELDS = [
  'wage', 'bankAccountHolder', 'bankName', 'sortCode', 'accountNumber',
  'niNumber', 'dateOfBirth', 'plandayId', 'xeroId',
  'tipJarReference', 'tipJarInviteSentAt', 'tipJarInviteSentTo',
] as const;

/** Fields managers may edit on employees */
export const MANAGER_SAFE_EDIT_FIELDS = [
  'role', 'department', 'position', 'skills', 'onboardingStatus',
  'active', 'employmentType', 'startDate', 'nextReviewDate',
  'probationStatus', 'pensionStatus', 'holidayBalance', 'holidayAllowance',
  'phoneNumber',
] as const;

/** All fields Owner/Admin may edit */
export const ADMIN_EDIT_FIELDS = [
  ...MANAGER_SAFE_EDIT_FIELDS,
  'wage', 'tipJarReference', 'plandayId', 'xeroId',
] as const;

/**
 * Mask a National Insurance number. Shows only last 3 characters.
 * Returns full value only for Owner/Admin.
 */
export function maskNiNumber(ni: string | undefined, viewerRole?: string): string {
  if (!ni) return '—';
  if (isOwnerOrAdmin(viewerRole)) return ni;
  if (ni.length <= 3) return '•••' + ni;
  return '•••••' + ni.slice(-3);
}

/**
 * Redact sensitive fields from an employee record for manager-level viewers.
 * Returns the record as-is for Owner/Admin.
 */
export function redactForManager<T extends Record<string, unknown>>(record: T, viewerRole?: string): T {
  if (isOwnerOrAdmin(viewerRole)) return record;
  const redacted = { ...record };
  for (const field of SENSITIVE_FIELDS) {
    if (field in redacted) {
      delete (redacted as any)[field];
    }
  }
  return redacted;
}
